import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ComponentProps } from 'react';
import { useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import type { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Share as ShareIcon } from '@sorare/core/src/atoms/icons/Share';
import { Drawer } from '@sorare/core/src/atoms/layout/Drawer';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { HeadlineM } from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import type { Props as SocialShareContentProps } from '@sorare/core/src/components/user/SocialShare/Content';
import { SocialShareContent } from '@sorare/core/src/components/user/SocialShare/Content';
import { SHARING_ORIGIN } from '@sorare/core/src/config';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useAbsoluteURL } from '@sorare/core/src/hooks/useAbsoluteURL';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import {
  socialShareEventContext,
  socialShareEventName,
} from '@sorare/core/src/lib/events';
import { glossary, navLabels } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import type { Share_socialPictureDerivative } from './__generated__/index.graphql';

const messages = defineMessages({
  checkOutMyClub: {
    id: 'Profile.Header.Share.checkOutMyClub',
    defaultMessage:
      'Check out my Club on @Sorare and follow my journey!\n{link}\n\n#OwnYourGame',
  },
  checkOutMyGallery: {
    id: 'Profile.Header.Share.checkOutMyGallery',
    defaultMessage: 'Check out my Gallery on @Sorare!\n{link}\n\n#OwnYourGame',
  },
  checkOutMyFavoritePlayers: {
    id: 'Profile.Header.Share.checkOutMyFavoritePlayers',
    defaultMessage:
      'Check out my favorite players on @Sorare!\n{link}\n\n#OwnYourGame',
  },
});

const StyledVertical = styled(Vertical)`
  padding: var(--double-unit);
  @media ${tabletAndAbove} {
    padding-top: 0;
  }
`;
const Wrapper = styled.div`
  border-radius: var(--double-unit) var(--double-unit) 0 0;
  background-color: var(--c-nd-100-opaque);
`;

type Props = {
  defaultTab?: TABS;
  managerProfileSocialPictures?: Share_socialPictureDerivative;
  managerProfilePath: string;
  gallerySocialPictures?: Share_socialPictureDerivative;
  galleryPath: string;
  fingerprint?: string;
  renderButton?: (
    buttonProps: ComponentProps<typeof ButtonBase>
  ) => React.JSX.Element;
};

export enum TABS {
  PROFILE = 'PROFILE',
  GALLERY = 'GALLERY',
  HIGHLIGHTED_DECK = 'HIGHLIGHTED_DECK',
}

type SocialShareProps = SocialShareContentProps & {
  message: string | ((link: string | undefined) => string);
  shareProps: {
    url: string;
  };
};

const toSocialShareObject = (props: SocialShareProps) => ({
  title: props.title,
  description: props.description,
  url: props.shareProps.url,
  image: props.image
    ? {
        post: props.image.post || undefined,
        story: props.image.story || undefined,
        square: props.image.square || undefined,
      }
    : undefined,
  message:
    typeof props.message === 'function'
      ? props.message(props.shareProps.url)
      : props.message,
  trackingEventName: props.shareProps.trackingEventName,
  trackingEventContext: props.shareProps.trackingEventContext,
  sharedItemId: undefined,
  sharedItem: undefined,
});

export const Share = ({
  defaultTab = TABS.PROFILE,
  managerProfilePath,
  managerProfileSocialPictures,
  galleryPath,
  gallerySocialPictures,
  fingerprint,
  renderButton,
}: Props) => {
  const { formatMessage } = useIntl();
  const { up: isTabletOrAbove } = useScreenSize('tablet');
  const [tab, setTab] = useState<TABS>(defaultTab);
  const [open, setOpen] = useState(false);
  const { isAndroidApp, postMessage } = useIsMobileApp();

  const fingerprintQuery = fingerprint ? `?t=${fingerprint}` : '';

  const managerProfileUrl = useAbsoluteURL(
    `${managerProfilePath}${fingerprintQuery || ''}`,
    {
      origin: SHARING_ORIGIN,
    }
  );
  const shareProfileProps: SocialShareProps = {
    image: managerProfileSocialPictures,
    message: link =>
      formatMessage(messages.checkOutMyClub, {
        link,
      }),
    shareProps: {
      url: managerProfileUrl,
      trackingEventName: socialShareEventName.SHARE_MANAGER_PROFILE,
      trackingEventContext: socialShareEventContext.MANAGER_PROFILE,
    },
  };

  const galleryUrl = useAbsoluteURL(`${galleryPath}${fingerprintQuery || ''}`, {
    origin: SHARING_ORIGIN,
  });
  const shareGalleryProps: SocialShareProps = {
    image: gallerySocialPictures,
    message: link =>
      formatMessage(messages.checkOutMyGallery, {
        link,
      }),
    shareProps: {
      url: galleryUrl,
      trackingEventName: socialShareEventName.SHARE_GALLERY,
      trackingEventContext: socialShareEventContext.MANAGER_PROFILE,
    },
  };

  const content = (
    <StyledVertical center gap={3}>
      <HeadlineM as="h3">
        <FormattedMessage {...glossary.share} />
      </HeadlineM>
      <TabBar value={tab} compact>
        <Tab
          key={TABS.PROFILE}
          value={TABS.PROFILE}
          onClick={() => setTab(TABS.PROFILE)}
        >
          <FormattedMessage {...navLabels.profile} />
        </Tab>
        <Tab
          key={TABS.GALLERY}
          value={TABS.GALLERY}
          onClick={() => setTab(TABS.GALLERY)}
        >
          <FormattedMessage {...navLabels.gallery} />
        </Tab>
      </TabBar>
      {tab === TABS.PROFILE && <SocialShareContent {...shareProfileProps} />}
      {tab === TABS.GALLERY && <SocialShareContent {...shareGalleryProps} />}
    </StyledVertical>
  );

  const onClick = () => {
    if (isAndroidApp) {
      postMessage('managerShare', {
        profile: toSocialShareObject(shareProfileProps),
        gallery: toSocialShareObject(shareGalleryProps),
        highlightedDeck: undefined,
      });
    } else {
      setOpen(true);
    }
  };

  if (!open && tab !== defaultTab) {
    setTab(defaultTab);
  }

  return (
    <>
      {renderButton?.({ onClick }) || (
        <Button
          size="medium"
          color="quaternary"
          startIcon={<ShareIcon />}
          onClick={onClick}
        >
          <FormattedMessage {...glossary.share} />
        </Button>
      )}
      {isTabletOrAbove ? (
        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          {content}
        </Dialog>
      ) : (
        <Drawer
          onBackdropClick={() => setOpen(false)}
          open={open}
          side="bottom"
        >
          <Wrapper>{content}</Wrapper>
        </Drawer>
      )}
    </>
  );
};

Share.fragments = {
  socialPictureDerivative: gql`
    fragment Share_socialPictureDerivative on SocialPictureDerivative {
      ...SocialShare_Content_socialPictureDerivative
    }
    ${SocialShareContent.fragments.socialPictures}
  ` as TypedDocumentNode<Share_socialPictureDerivative>,
};
